import { useEffect, useState } from 'react';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { useAppStore } from 'src/hooks/useAppStore';
import {
  addItemToWatchlist,
  fetchWatchlistItems,
  removeItemFromWatchlist
} from '../../../services/apiRequests/WatchlistService';
import { Episode } from '../../../services/GraphQL/Queries/types';
import { Posts } from '../../../types/tvshowdetails';
import {
  MIXPANEL_ADD_WATCHLIST,
  MIXPANEL_REMOVE_WATCHLIST
} from '../../../utils/analytics/mixpanel/mixpanelConstants';
import {
  getLocalStorage,
  getSessionStorage
} from '../../../utils/browserStorageHandler';
import {
  generateCardsUrl,
  navigationHandler
} from '../../../utils/cardsUrlGenerator';
import { ContentItemType } from '../../../utils/commonTypes';
import { DEVICE_TYPES } from '../../../utils/constants/AppConstants';
import { HTTP_HEADER_KEYS } from '../../../utils/constants/HttpMethodsConstants';
import {
  REDIRECT_URL_TO_CONSUMPTION,
  USER_TOKEN,
  WATCH_LIST_SESSION_STORAGE_OBJECT
} from '../../../utils/constants/localStorageConstants';
import { LOGIN_TO_ZEE5 } from '../../../utils/constants/stringConstants';
import {
  AssetsSubType,
  WatchListPayload
} from '../../../utils/constants/TvShowDetailsPageConstants';
import { ContentMetaData } from '../../../utils/ConsumptionHelper/types';
import { triggerMixpanelForThumbnailClick } from '../../../utils/contentCardHandler';
import {
  isTypePost,
  storiesPropsGenerator,
  thumbnailPropsGenerator
} from '../../../utils/ThumbnailComponentHelper/ThumbnailComponentHelper';
import {
  addToWatchlistMixpanelEvent,
  addWatchlistItem,
  checkIfContentAvailableInWatchlist,
  removeFromWatchlistMixpanelEvent,
  removeWatchlistItem
} from '../../MetaButtons/WatchlistBtn/WatchlistHandler';
import { BreakPointsConfig } from '../../rails/railsHelper';
import { CardDetailsType } from '../../thumbnail/types';
import ToastMessage from '../../toastMessage';
import WatchlistLoginCheck from '../../watchlistLoginCheckPopup/WatchlistLoginCheckPopup';

const Thumbnail = dynamic(() => import('@wds/thumbnail'), {
  loading: () => null,
  ssr: false
});
const WdsShare = dynamic(() => import('../wdsShare/wdsShare'), {
  loading: () => null,
  ssr: false
});

type ThumbnailHOCPropType = {
  details: Episode | Posts;
  breakPointsConfig?: BreakPointsConfig | null | undefined;
  index?: number;
};
const WdsThumbnail = ({
  details,
  breakPointsConfig = null,
  index = 1
}: ThumbnailHOCPropType) => {
  const router = useRouter();

  const {
    appConfig,
    usersLocationData,
    userWatchlist,
    translations,
    setUserWatchlist
  } = useAppStore();
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState<boolean>(false);
  const [detailsObject, setDetailsObject] = useState<WatchListPayload>();
  const [toastMsg, setToastMsg] = useState('');
  const translationData = translations?.consumption;
  const { ADD_WATCHLIST, REMOVE_WATCHLIST } = translationData || {};
  const translationText = {
    EPISODE_TEXT: translationData?.DETAILS.EPISODE,
    READ_MORE_TEXT: translations?.tvshowdetails?.READ_MORE_TEXT,
    MOVIE_TEXT: translationData?.BREADCRUMB?.MOVIES,
    WEB_SERIES_TEXT: translationData?.BREADCRUMB?.WEBSERIES
  };
  const isLoggedIn = !!getLocalStorage(USER_TOKEN);
  // when the user will be redirected from pwa to webapp we will perform this operation
  const addToWatchListPostRedirection = async () => {
    // Get details data from session storage
    const getDetailsDataFromSessionStorage = getSessionStorage(
      WATCH_LIST_SESSION_STORAGE_OBJECT
    );

    // Check if the details id matches with the id from session storage
    if (details.id !== getDetailsDataFromSessionStorage.id) {
      return;
    }

    // Check if the content is not already in the watchlist
    if (!checkIfContentAvailableInWatchlist(userWatchlist, details)) {
      const headers = [
        HTTP_HEADER_KEYS.X_ACCESS_TOKEN,
        HTTP_HEADER_KEYS.AUTHORIZATION
      ];

      // Add item to watchlist
      const response = await addItemToWatchlist(
        getDetailsDataFromSessionStorage,
        headers
      );

      // If adding to watchlist was successful
      if (response?.code === 1) {
        setToastMsg(ADD_WATCHLIST);
      }

      // Fetch updated watchlist items
      const watchlistResponse = await fetchWatchlistItems(headers);

      // Update user watchlist
      setUserWatchlist({ ...watchlistResponse });

      // Track event in Mixpanel
      addToWatchlistMixpanelEvent(details as ContentItemType);
    }
  };

  useEffect(() => {
    const redirectionFromConsumption = getSessionStorage(
      REDIRECT_URL_TO_CONSUMPTION
    );
    if (redirectionFromConsumption) {
      addToWatchListPostRedirection();
    }
  }, [isLoggedIn, userWatchlist?.isWatchlistDataFetched]);

  const watchCallback = () => {
    details = details as Episode;
    let slugData = details ? generateCardsUrl(details) ?? details?.webSlug : '';
    if (slugData) {
      slugData = slugData.startsWith('/') ? slugData : `/${slugData}`;
      if (
        appConfig.isRequestedByBot &&
        usersLocationData?.country_code !== 'IN'
      ) {
        const globalRegex = /\/global\//;
        slugData = slugData
          .replace(globalRegex, '/')
          .replace(`/${router.locale}/`, '/');
      }
    }
    const mixpanelDetails: CardDetailsType = {
      contentId: details.id || '',
      title: details.title || '',
      horizontalIndex: 0,
      verticalIndex: 0,
      assetSubtype: details.assetSubType || AssetsSubType.TVSHOW,
      originalTitle: details.originalTitle || ''
    };
    if (slugData) {
      if (details && mixpanelDetails) {
        triggerMixpanelForThumbnailClick(
          details as ContentMetaData,
          mixpanelDetails,
          props.image?.horizontal || ''
        );
      }
      navigationHandler(slugData, (slugData) =>
        router.push(`/${slugData}`, `/${slugData}`)
      );
    }
  };
  const watchlistCallback = () => {
    if (checkIfContentAvailableInWatchlist(userWatchlist, details)) {
      // remove from watch list
      removeFromWatchlist();
    } else {
      // add to watch list
      addToWatchlist();
    }
  };

  const addToWatchlist = async () => {
    details = details as ContentItemType;
    const headers = [
      HTTP_HEADER_KEYS.X_ACCESS_TOKEN,
      HTTP_HEADER_KEYS.AUTHORIZATION
    ];
    const requestObj = {
      id: details.id || '',
      assetType: details.assetType as number,
      duration: details.duration || 0,
      date: details.releaseDate || new Date().toISOString()
    };
    if (isLoggedIn) {
      const response = await addItemToWatchlist(requestObj, headers);
      if (response?.code === 1) {
        setToastMsg(ADD_WATCHLIST);
      }
      const updatedWatchlistData = addWatchlistItem(
        userWatchlist,
        details as ContentItemType
      );
      setUserWatchlist(updatedWatchlistData);
      addToWatchlistMixpanelEvent(details as ContentItemType);
    } else {
      setToastMsg(translations?.loginRegister?.LOGIN_INFO || LOGIN_TO_ZEE5);
      setDetailsObject(requestObj);
      setShowLoginPopup(true);
    }
  };

  const removeFromWatchlist = async () => {
    const headers = [
      HTTP_HEADER_KEYS.X_ACCESS_TOKEN,
      HTTP_HEADER_KEYS.AUTHORIZATION
    ];
    details = details as Episode;
    const requestObj = {
      id: details?.id || '',
      assetType: details?.assetType as number
    };
    const response = await removeItemFromWatchlist(requestObj, headers);
    if (response?.code === 1) {
      setToastMsg(REMOVE_WATCHLIST);
    }
    const updatedWatchlistData = removeWatchlistItem(
      userWatchlist,
      details as ContentItemType
    );
    setUserWatchlist(updatedWatchlistData);
    removeFromWatchlistMixpanelEvent(details as ContentItemType);
  };

  const props = isTypePost(details)
    ? storiesPropsGenerator({
        post: details,
        index,
        breakPointsConfig,
        translationText,
        deviceType: appConfig.deviceType || DEVICE_TYPES.DESKTOP,
        onShareClick: () => {
          setShowSharePopup(true);
        }
      })
    : thumbnailPropsGenerator({
        data: details,
        // TODO :: Add checks for assets where add to watchlist is not available with new DS @wds/cards
        showAddToWatchlistButton: true,
        isAvailableOnWatchlist:
          checkIfContentAvailableInWatchlist(userWatchlist, details) || false,
        onCardClick: watchCallback,
        onWatchlistClick: watchlistCallback,
        onShareClick: () => {
          setShowSharePopup(true);
        },
        translationText: translationText,
        breakPointsConfig,
        index,
        deviceType: appConfig.deviceType || DEVICE_TYPES.DESKTOP
      });
  const closePopup = () => {
    setShowSharePopup(false);
  };
  const closeLoginPopup = () => {
    setShowLoginPopup(false);
  };

  useEffect(() => {
    if (toastMsg) {
      setTimeout(() => {
        setToastMsg('');
      }, 2000);
    }
  }, [toastMsg]);

  return (
    <>
      <Thumbnail {...props} />
      {showSharePopup && <WdsShare closePopup={closePopup} details={details} />}
      {toastMsg ? (
        <ToastMessage
          message={toastMsg}
          mixpanelMessage={
            toastMsg === ADD_WATCHLIST
              ? MIXPANEL_ADD_WATCHLIST
              : MIXPANEL_REMOVE_WATCHLIST
          }
          data-testid="wdsThumbnailToastMessage"
        />
      ) : null}
      {showLoginPopup && (
        <WatchlistLoginCheck
          show={showLoginPopup}
          onClose={closeLoginPopup}
          detailsObject={detailsObject}
        />
      )}
    </>
  );
};

export default WdsThumbnail;
